/**
 * Статус работы по контурам и хозяйствам — чистая логика страницы работы
 * (agroai-6al.35). Источник — `GET /season/contour-status?campaign_id=`: по
 * каждому контуру сервер уже свернул статус его обязательств этой работы и
 * назвал число обязательств в каждом статусе.
 *
 * Пять исходов, и они не сливаются (журнал решений №290):
 *   - «Выполнено» — внесён отчёт «выполнено» (факт по умолчанию, без проверки);
 *   - «В процессе» — отчёт есть, но говорит «работа начата, не закончена»;
 *   - «Нет отчёта · окно идёт» — ещё не срок;
 *   - «После срока · нет отчёта» — окно закрыто, отчёта нет: повод уточнить,
 *     а не вывод «не сделано»;
 *   - «Окно впереди».
 */
import type { Schemas } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import { runtimeLocale, tr } from '../../../i18n/runtime';
import type { WorkStatusValue } from '../../map/paint';

const m = defineMessages({
  doneLabel: { id: 'work.status.done.label', defaultMessage: 'Выполнено', description: 'Статус контура в работе: внесён отчёт «выполнено»; подпись легенды карты и бейдж хозяйства' },
  doneNote: { id: 'work.status.done.note', defaultMessage: 'внесён отчёт «выполнено»', description: 'Пояснение к статусу «Выполнено» в легенде карты работы' },
  doneShort: { id: 'work.status.done.short', defaultMessage: 'выполнено', description: 'Короткая подпись статуса «Выполнено» в строке «контуры хозяйства по статусу» (строчными, после числа)' },
  progressLabel: { id: 'work.status.progress.label', defaultMessage: 'В процессе', description: 'Статус контура в работе: отчёт есть, но работа не закончена; подпись легенды карты и бейдж хозяйства' },
  progressNote: { id: 'work.status.progress.note', defaultMessage: 'отчёт есть, работа не закончена', description: 'Пояснение к статусу «В процессе» в легенде карты работы' },
  progressShort: { id: 'work.status.progress.short', defaultMessage: 'в процессе', description: 'Короткая подпись статуса «В процессе» в строке «контуры хозяйства по статусу» (строчными, после числа)' },
  windowLabel: { id: 'work.status.window.label', defaultMessage: 'Нет отчёта · окно идёт', description: 'Статус контура в работе: окно идёт, отчёта пока нет; подпись легенды карты и бейдж хозяйства' },
  windowNote: { id: 'work.status.window.note', defaultMessage: 'ещё не срок', description: 'Пояснение к статусу «Нет отчёта · окно идёт» в легенде карты работы' },
  windowShort: { id: 'work.status.window.short', defaultMessage: 'нет отчёта', description: 'Короткая подпись статуса «Нет отчёта · окно идёт» в строке «контуры хозяйства по статусу» (строчными, после числа)' },
  overdueLabel: { id: 'work.status.overdue.label', defaultMessage: 'После срока · нет отчёта', description: 'Статус контура в работе: окно закрыто, отчёта нет; подпись легенды карты и бейдж хозяйства' },
  overdueNote: { id: 'work.status.overdue.note', defaultMessage: 'окно закрыто, отчёта нет — повод уточнить, а не вывод «не сделано»', description: 'Пояснение к статусу «После срока · нет отчёта» в легенде карты работы' },
  overdueShort: { id: 'work.status.overdue.short', defaultMessage: 'после срока', description: 'Короткая подпись статуса «После срока · нет отчёта» в строке «контуры хозяйства по статусу» (строчными, после числа)' },
  awaitingLabel: { id: 'work.status.awaiting.label', defaultMessage: 'Окно впереди', description: 'Статус контура в работе: окно ещё не открылось; подпись легенды карты и бейдж хозяйства' },
  awaitingNote: { id: 'work.status.awaiting.note', defaultMessage: 'работа ещё не началась', description: 'Пояснение к статусу «Окно впереди» в легенде карты работы' },
  awaitingShort: { id: 'work.status.awaiting.short', defaultMessage: 'окно впереди', description: 'Короткая подпись статуса «Окно впереди» в строке «контуры хозяйства по статусу» (строчными, после числа)' },
  farmNo: { id: 'work.status.farmNo', defaultMessage: 'Хозяйство №{id}', description: 'Название хозяйства, когда сервер имени не дал; {id} — служебный номер хозяйства' },
});

export type ContourStatusRow = Schemas['ContourStatusRowOut'];
export type WorkContourStatus = 'done' | 'in_progress' | 'in_window' | 'overdue' | 'awaiting';

/** Порядок показа: от сделанного к требующему внимания. */
export const STATUS_ORDER: WorkContourStatus[] = ['done', 'in_progress', 'in_window', 'overdue', 'awaiting'];

/** Порядок свёртки хозяйства: худший статус его контуров называет хозяйство. */
const FOLD: WorkContourStatus[] = ['overdue', 'in_window', 'awaiting', 'in_progress', 'done'];

export interface StatusMeta {
  /** Полная подпись статуса: легенда, бейдж. */
  label: string;
  /** Короткая строчная подпись статуса: «5 выполнено · 2 после срока». */
  short: string;
  note: string;
  /** Значение общего слоя карты `workStatus` — цвет и легенда кадра. */
  layer: WorkStatusValue;
  /** Токен образца для легенды и значков страницы (тот же цвет, что у слоя). */
  swatch: string;
}

/**
 * Подписи — геттеры: строка берётся при обращении, на языке интерфейса в этот
 * момент (константа модуля застыла бы на языке загрузки).
 */
export const STATUS_META: Record<WorkContourStatus, StatusMeta> = {
  done: {
    get label() { return tr(m.doneLabel); },
    get short() { return tr(m.doneShort); },
    get note() { return tr(m.doneNote); },
    layer: 'reported',
    swatch: '--st-ok',
  },
  in_progress: {
    get label() { return tr(m.progressLabel); },
    get short() { return tr(m.progressShort); },
    get note() { return tr(m.progressNote); },
    layer: 'in_progress',
    swatch: '--data-ok-started',
  },
  in_window: {
    get label() { return tr(m.windowLabel); },
    get short() { return tr(m.windowShort); },
    get note() { return tr(m.windowNote); },
    layer: 'in_window',
    swatch: '--st-progress',
  },
  overdue: {
    get label() { return tr(m.overdueLabel); },
    get short() { return tr(m.overdueShort); },
    get note() { return tr(m.overdueNote); },
    layer: 'silent',
    swatch: '--st-missing',
  },
  awaiting: {
    get label() { return tr(m.awaitingLabel); },
    get short() { return tr(m.awaitingShort); },
    get note() { return tr(m.awaitingNote); },
    layer: 'upcoming',
    swatch: '--st-future',
  },
};

/** Статус сервера → статус страницы. Незнакомый — `null`: контур не красится наугад. */
export function fromServer(status: string): WorkContourStatus | null {
  switch (status) {
    case 'reported':
      return 'done';
    case 'in_progress':
      return 'in_progress';
    case 'in_window':
      return 'in_window';
    case 'overdue':
      return 'overdue';
    case 'awaiting_window':
      return 'awaiting';
    default:
      return null;
  }
}

export interface StatusTotal {
  status: WorkContourStatus;
  contours: number;
  /** Обязательства в этом статусе (по строкам `by_status`). */
  obligations: number;
  /** Гектары контуров статуса; `null` — ни у одного площадь не названа. */
  ha: number | null;
  /** Контуров статуса без площади в реестре. */
  haMissing: number;
}

const HA = 1_000_000;

/** Сводка по статусам: контуры (свёрнутые), обязательства и гектары. */
export function statusTotals(rows: readonly ContourStatusRow[]): Record<WorkContourStatus, StatusTotal> {
  const out = Object.fromEntries(
    STATUS_ORDER.map(s => [s, { status: s, contours: 0, obligations: 0, ha: null, haMissing: 0 } satisfies StatusTotal]),
  ) as Record<WorkContourStatus, StatusTotal>;
  for (const row of rows) {
    const status = fromServer(row.status);
    if (!status) continue;
    const t = out[status];
    t.contours += 1;
    if (row.area_microha === null || row.area_microha === undefined) t.haMissing += 1;
    else t.ha = (t.ha ?? 0) + row.area_microha / HA;
    const by = row.by_status;
    out.done.obligations += by.reported;
    out.in_progress.obligations += by.in_progress ?? 0;
    out.in_window.obligations += by.in_window;
    out.overdue.obligations += by.overdue;
    out.awaiting.obligations += by.awaiting_window;
  }
  return out;
}

/** Значения слоя карты по контурам; `only` — показать только эти статусы. */
export function layerById(rows: readonly ContourStatusRow[]): Record<string, WorkStatusValue> {
  const out: Record<string, WorkStatusValue> = {};
  for (const row of rows) {
    const status = fromServer(row.status);
    if (status) out[String(row.contour_id)] = STATUS_META[status].layer;
  }
  return out;
}

/** Контуры выбранных статусов; пустой выбор — все контуры работы. */
export function contoursOf(rows: readonly ContourStatusRow[], only: ReadonlySet<WorkContourStatus>): string[] {
  return rows
    .filter(r => {
      const s = fromServer(r.status);
      return s !== null && (only.size === 0 || only.has(s));
    })
    .map(r => String(r.contour_id));
}

export interface FarmStatusRow {
  actorId: number;
  name: string;
  /** Худший статус контуров хозяйства в этой работе. */
  status: WorkContourStatus;
  contours: number;
  byStatus: Record<WorkContourStatus, number>;
  ha: number | null;
  contourIds: string[];
}

/** Хозяйства работы с их статусом — по контурам того же ответа, что и карта. */
export function farmRows(rows: readonly ContourStatusRow[], names: ReadonlyMap<number, string>): FarmStatusRow[] {
  const by = new Map<number, FarmStatusRow>();
  for (const row of rows) {
    for (const actorId of row.actor_ids) {
      // Статус контура для этого хозяйства — свёртка только его обязательств
      // (agroai-0e8): чужое «нет отчёта» на общем контуре не красит соседа.
      const status = fromServer(row.actor_status?.[String(actorId)] ?? row.status);
      if (!status) continue;
      let farm = by.get(actorId);
      if (!farm) {
        farm = {
          actorId,
          name: names.get(actorId) ?? tr(m.farmNo, { id: String(actorId) }),
          status: 'done',
          contours: 0,
          byStatus: { done: 0, in_progress: 0, in_window: 0, overdue: 0, awaiting: 0 },
          ha: null,
          contourIds: [],
        };
        by.set(actorId, farm);
      }
      farm.contours += 1;
      farm.byStatus[status] += 1;
      farm.contourIds.push(String(row.contour_id));
      if (row.area_microha !== null && row.area_microha !== undefined) farm.ha = (farm.ha ?? 0) + row.area_microha / HA;
    }
  }
  for (const farm of by.values()) farm.status = FOLD.find(s => farm.byStatus[s] > 0) ?? 'done';
  return [...by.values()].sort(
    (a, b) => FOLD.indexOf(a.status) - FOLD.indexOf(b.status) || b.byStatus[a.status] - a.byStatus[a.status] || a.name.localeCompare(b.name, runtimeLocale() === 'ru' ? 'ru' : runtimeLocale()),
  );
}

/** Сколько хозяйств в каждом статусе (по худшему контуру). */
export function farmCounts(farms: readonly FarmStatusRow[]): Record<WorkContourStatus, number> {
  const out: Record<WorkContourStatus, number> = { done: 0, in_progress: 0, in_window: 0, overdue: 0, awaiting: 0 };
  for (const f of farms) out[f.status] += 1;
  return out;
}
